import React, { useEffect, useReducer, useRef } from 'react';
import {
  Button, ButtonToolbar, Form, FormInstance, InputPicker, Loader, Stack,
} from 'rsuite';
import { defaultTo, find } from 'lodash';
import axios from 'axios';
import { ItemDataType } from 'rsuite/CascadeTree';
import { SalesEntity } from '../../api/apiTypes';
import { Eshop } from '../../api/eshop/eshop';
import {
  getAllSalesEntities,
  getSalesEntityForPartnerEshop,
  updateSalesEntityForPartnerEshop,
} from '../../api/salesentity/salesEntity';
import { showSuccessMessage } from '../../utils/message';
import { useStoreState } from '../../store/hooks';
import { Permission } from '../../store/user/types';

interface Props {
  eshop: Eshop;
}

interface Edit {
  code: string;
}

type Status = 'ready' | 'loading' | 'saving';

interface State {
  status: Status;
  salesEntities: SalesEntity[];
  salesEntity?: SalesEntity;
  edit: Edit;
}

const initialState: State = {
  status: 'ready',
  salesEntities: [],
  edit: {
    code: 'PARTNERS_OWN',
  },
};

type StatusSetAction = { type: 'status/set', payload: Status };
type SalesEntitiesSetAction = { type: 'salesEntities/set', payload: SalesEntity[] };
type SalesEntitySetAction = { type: 'salesEntity/set', payload: SalesEntity };
type EditSetAction = { type: 'edit/set', payload: Partial<Edit> };
type EditResetAction = { type: 'edit/reset' };
type Action =
  | StatusSetAction
  | SalesEntitiesSetAction
  | SalesEntitySetAction
  | EditSetAction
  | EditResetAction;

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'status/set': {
      return { ...state, status: action.payload };
    }
    case 'salesEntities/set': {
      return {
        ...state,
        salesEntities: action.payload,
      };
    }
    case 'salesEntity/set':
      return {
        ...state,
        salesEntity: action.payload,
        edit: {
          ...state.edit,
          code: action.payload.code,
        },
      };
    case 'edit/set':
      return { ...state, edit: { ...state.edit, ...action.payload } };
    case 'edit/reset':
      return {
        ...state,
        edit: {
          code: defaultTo(
            state.salesEntity?.code,
            initialState.edit.code,
          ),
        },
      };
    default:
      return state;
  }
}

function mapSalesEntities(salesEntities: SalesEntity[]): ItemDataType[] {
  return salesEntities.map((salesEntity) => ({
    value: salesEntity.code,
    label: salesEntity.name,
  }));
}

export default function EshopSetupPartnerSalesEntity({ eshop }: Readonly<Props>): JSX.Element {
  const [state, dispatch] = useReducer(reducer, initialState);
  const abortControllerRef = useRef<AbortController>(new AbortController());
  const formRef = useRef<FormInstance>(null);

  const currentUser = useStoreState((storeState) => storeState.user);
  const hasPermission = currentUser.hasPermission(Permission.PartnerSalesEntity);

  async function fetchAllSalesEntities(signal: AbortSignal): Promise<SalesEntity[]> {
    const salesEntities = await getAllSalesEntities(signal);
    dispatch({ type: 'salesEntities/set', payload: salesEntities });
    return salesEntities;
  }

  async function fetchCurrentSalesEntity(
    salesEntities: SalesEntity[],
    signal: AbortSignal,
  ): Promise<void> {
    let salesEntity: SalesEntity | undefined;

    try {
      salesEntity = await getSalesEntityForPartnerEshop(eshop.code, signal);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        salesEntity = find(salesEntities, { code: 'PARTNERS_OWN' });
      } else {
        throw error;
      }
    }

    if (salesEntity) {
      dispatch({ type: 'salesEntity/set', payload: salesEntity });
    }
  }

  async function fetchData(signal: AbortSignal): Promise<void> {
    dispatch({ type: 'status/set', payload: 'loading' });

    try {
      const salesEntities = await fetchAllSalesEntities(signal);
      await fetchCurrentSalesEntity(salesEntities, signal);
    } finally {
      dispatch({ type: 'status/set', payload: 'ready' });
    }
  }

  async function saveSalesEntity(): Promise<void> {
    if (abortControllerRef.current.signal.aborted) {
      abortControllerRef.current = new AbortController();
    }

    dispatch({ type: 'status/set', payload: 'saving' });

    const selectedEntity = find(state.salesEntities, { code: state.edit.code });

    if (!selectedEntity) {
      dispatch({ type: 'status/set', payload: 'ready' });
      return;
    }

    try {
      await updateSalesEntityForPartnerEshop(
        eshop.code,
        selectedEntity,
        abortControllerRef.current.signal,
      );
      dispatch({ type: 'salesEntity/set', payload: selectedEntity });
      showSuccessMessage("Partner's business entity saved.");
    } finally {
      dispatch({ type: 'status/set', payload: 'ready' });
    }
  }

  function handleChange(edit: Record<string, any>): void {
    dispatch({ type: 'edit/set', payload: edit as Partial<Edit> });
  }

  function handleSave() {
    saveSalesEntity().then();
  }

  function handleCancel(): void {
    dispatch({ type: 'edit/reset' });
  }

  useEffect(
    () => () => {
      abortControllerRef.current.abort();
    },
    [abortControllerRef.current],
  );

  useEffect(() => {
    const abortController = new AbortController();

    fetchData(abortController.signal).then();

    return () => {
      abortController.abort();
    };
  }, [eshop]);

  if (state.status === 'loading') {
    return <Loader />;
  }

  const salesEntities = mapSalesEntities(state.salesEntities);

  return (
    <Stack>
      <Form
        layout="horizontal"
        ref={formRef}
        formValue={state.edit}
        onChange={(edit) => handleChange(edit)}
      >
        <Form.Group>
          <Form.ControlLabel>
            Select business entity
          </Form.ControlLabel>
          <Form.Control
            name="code"
            accepter={InputPicker}
            data={salesEntities}
            cleanable={false}
            disabled={!hasPermission}
          />
        </Form.Group>
        <Form.Group>
          <ButtonToolbar>
            <Button
              appearance="primary"
              onClick={() => handleSave()}
              type="submit"
              loading={state.status === 'saving'}
              disabled={!hasPermission}
            >
              Save
            </Button>
            <Button
              appearance="ghost"
              onClick={() => handleCancel()}
              disabled={!hasPermission}
            >
              Cancel
            </Button>
          </ButtonToolbar>
        </Form.Group>
      </Form>
    </Stack>
  );
}
